import React, { useState, useEffect } from "react"
import {CONVERTIBLE_CURRENCIES, themeColors, radius} from '../constants'
import { Star, CurrencyRow } from '../components'

// Multi-select of 常用 (favorite) currencies for an accounting book, styled like
// the payment-page CurrencyDropdown: two sections (常用 on top, 其他 below) with
// a ★/☆ star to toggle. Tapping a row stars it and moves it up into 常用. The
// book's own settlement currency is always implicitly available, so excluded.
const useFavoriteCurrenciesSelect = ({
  initialValue = [],
  excludeCurrency,
  callback
}) => {
  const [values, setValues] = useState(initialValue)
  const [query, setQuery] = useState('')

  // Sync only when the *contents* change. Depending on the array reference would
  // loop forever because callers pass a fresh `[]` literal on every render.
  const initialKey = JSON.stringify(initialValue || [])
  useEffect(() => {
    setValues(initialValue || [])
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [initialKey])

  const toggle = (code) => {
    const next = values.includes(code)
      ? values.filter((c) => c !== code)
      : [...values, code]
    setValues(next)
    if (callback) { callback(next) }
  }

  const all = CONVERTIBLE_CURRENCIES.filter((c) => c.code !== excludeCurrency)
  const favList = all.filter((c) => values.includes(c.code))
  const otherList = all.filter((c) => !values.includes(c.code))

  const q = query.trim().toLowerCase()
  const matches = (c) => c.code.toLowerCase().includes(q) || (c.label || '').includes(query.trim())

  const Row = (o) => {
    const active = values.includes(o.code)
    return (
      <button
        type="button"
        key={o.code}
        onClick={() => toggle(o.code)}
        style={{ ...styles.row, ...(active ? styles.rowActive : {}) }}
      >
        <CurrencyRow flag={o.flag} code={o.code} label={o.label} />
        <Star solid={active} color={themeColors.gold700} />
      </button>
    )
  }

  const select = (
    <div>
      <div style={styles.searchWrap}>
        <span style={{ marginRight: 8 }}>🔍</span>
        <input
          style={styles.search}
          type="text"
          placeholder="搜尋幣別（USD、美元…）"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div style={styles.list}>
        {q ? (
          all.filter(matches).length === 0
            ? <div style={styles.empty}>找不到符合的幣別</div>
            : all.filter(matches).map(Row)
        ) : (
          <>
            {favList.length > 0 ? <div style={styles.sectionHead}>常用</div> : null}
            {favList.map(Row)}
            <div style={styles.sectionHead}>其他</div>
            {otherList.map(Row)}
          </>
        )}
      </div>
    </div>
  )

  return [values, select]
}

export default useFavoriteCurrenciesSelect

const styles = {
  searchWrap: {
    position: 'sticky', top: 0, zIndex: 1,
    display: 'flex', alignItems: 'center',
    background: themeColors.gray300, borderRadius: radius.md, padding: '8px 12px', margin: '8px',
  },
  search: { border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: '14px' },
  list: { display: 'flex', flexDirection: 'column', padding: '0 0 8px' },
  sectionHead: { fontSize: '12px', fontWeight: 600, color: themeColors.gray600, padding: '12px 20px 4px' },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
    background: 'transparent', border: 'none', padding: '12px 20px', cursor: 'pointer',
  },
  rowActive: { background: 'rgba(200, 132, 42, 0.10)' },
  flag: { fontSize: '22px', paddingRight: '12px' },
  code: { fontWeight: 600, paddingRight: '10px', color: themeColors.gray900 },
  label: { color: themeColors.gray700, flex: 1, textAlign: 'left' },
  star: { fontSize: '18px', color: themeColors.gold700, padding: '0 4px' },
  empty: { textAlign: 'center', color: themeColors.gray600, padding: '24px' },
}
